import { AlertCircle, Diamond } from '@saleshandy/icons';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { handle3dSecureCardPayment } from '../../../shared/components/handle-3d-secure-card-payment/handle3dSecureCardPayment';
import ImportEmailVerification from '../../../shared/design-system/components/molecules/email-verification-content/import-email-verification';
import Modal from '../../../shared/design-system/ui/modal';
import { RequestStatus } from '../../../shared/enums/request-status';
import toaster , { Theme } from '../../../shared/toaster';
import { executeOnRequestStatus } from '../../../shared/utils';
import { accessibleOnClick } from '../../../shared/utils/accessible-on-click';
import ImportStageTabs from '../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/import-flow/components/import-stage-tabs';
import { Stage } from '../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/import-flow/stage';
import { ImportConfigDataForEmailVerification } from '../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/import-flow/types';
import MapFields from '../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/map-fields';
import { RowDataWithTypeVerification } from '../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/map-fields/types';
import { ConflictAction } from '../../sequence/enums';
import { EmailMapConfig, MapConfig } from '../../sequence/types';
import PurchaseCreditsModal from '../../settings/components/billing-subscription/components/purchase-credits-modal';
import { FileUploadProps } from './type';
import { PurchaseCreditsType } from '../../settings/components/billing-subscription/types/purchase-credits';
import useCreditsTransactionPolling from '../../../shared/hooks/useCreditsTransactionPolling';

const FileUpload: React.FC<FileUploadProps> = memo(
  ({
    currentPlanName,
    getEmailVerificationPlanResponse,
    sendPurchaseEmailVerificationCreditsRequestStatus,
    getPurchaseEmailVerificationCreditsResponse,
    sendPurchaseEmailVerificationCreditsRequestMessage,
    fileData,
    setFileData,
    stage,
    setStage,
    showModal,
    setShowModal,
    showPurchaseCreditModel,
    setShowPurchaseCreditModel,
    resetPurchaseEmailVerificationCreditsResponses,
    getContactFieldsRequests,
    getEmailVerificationCreditsRequests,
    uploadedFileCsvRequests,
    resetUploadedFileCsvRequests,
    enableBulkActionRequestPollings,
    purchaseEmailVerificationCreditsRequests,
    contactField,
    emailVerifyCredits,
    uploadedFileCsvRequestStatus,
    setSearchPagination,
    getCreditsTransactionStatusRequestStatus,
    getCreditsTransactionStatusResponse,
    sendGetCreditsTransactionStatusRequest,
    resetGetCreditsTransactionStatusRequest,
    resetPurchaseCreditsRequest,
  }) => {
    const [isVerifyingEmail, setIsVerifyingEmail] = useState<boolean>(false);
    const [isPolling, setIsPolling] = useState<boolean>(false);
    const [orderId, setOrderId] = useState<string | undefined>();
    const [
      importConfigParam,
      setImportConfigParam,
    ] = useState<ImportConfigDataForEmailVerification>(null);
    const [
      is3dSecureResponseStatePending,
      setIs3dSecureResponseStatePending,
    ] = useState<boolean>(false);

    const emailFilteredFields = useMemo(
      () =>
        contactField?.filter(
          (field) => field?.label?.replace(/\s/g, '').toLowerCase() === 'email',
        ) || [],
      [contactField],
    );

    const onStageChange = useCallback((newState: Stage) => {
      setStage(newState);
    }, []);

    const hidePurchaseCreditsModal = useCallback(() => {
      setShowPurchaseCreditModel(false);
    }, [setShowPurchaseCreditModel]);

    const handle3dSecureCard = useCallback(
      async (response) => {
        const apiEndPoint =
          '/email-verification-credits/confirm-payment-intent';
        setIs3dSecureResponseStatePending(true);

        const { isError, serverResponse } = await handle3dSecureCardPayment({
          response,
          apiEndPoint,
        });

        setIs3dSecureResponseStatePending(false);

        if (isError) {
          toaster.error('Payment failed! Please retry again after some time.');
        } else {
          toaster.success(serverResponse?.payload?.message);
          hidePurchaseCreditsModal();
          resetPurchaseEmailVerificationCreditsResponses();
        }
      },
      [hidePurchaseCreditsModal],
    );

    // Handle Paddle checkout complete - start polling
    const handleCheckoutComplete = useCallback((purchaseOrderId?: string) => {
      setOrderId(purchaseOrderId);
      setIsPolling(true);
    }, []);

    // Handle transaction success
    const handleTransactionSuccess = useCallback(() => {
      toaster.success('Credits purchased successfully!', {
        theme: Theme.New,
      });
      // Refresh email verification credits after successful purchase
      getEmailVerificationCreditsRequests();
    }, [getEmailVerificationCreditsRequests]);

    // Handle transaction failure
    const handleTransactionFailed = useCallback(() => {
      toaster.error('Payment failed. Please try again.');
    }, []);

    // Handle polling complete
    const handlePollingComplete = useCallback(() => {
      setIsPolling(false);
      setOrderId(undefined);
    }, []);

    // Use polling hook
    useCreditsTransactionPolling({
      isPolling,
      type: PurchaseCreditsType.EV_CREDITS,
      orderId,
      transactionStatusRequestStatus: getCreditsTransactionStatusRequestStatus,
      transactionStatusResponse: getCreditsTransactionStatusResponse,
      sendGetCreditsTransactionStatusRequest,
      resetPurchaseCreditsRequest,
      resetGetCreditsTransactionStatusRequest,
      onTransactionSuccess: handleTransactionSuccess,
      onTransactionFailed: handleTransactionFailed,
      onPollingComplete: handlePollingComplete,
    });

    const mapFieldsFinishHandler = useCallback(
      async (
        mapConfig: MapConfig,
        conflictAction: ConflictAction,
        stepId: number,
      ) => {
        const transformedMap = Object.fromEntries(
          Object.keys(mapConfig).map((key) => [Number(key), 'email']),
        ) as EmailMapConfig;

        setImportConfigParam({
          file: fileData?.file,
          mapConfig: transformedMap,
          conflictAction,
          stepId,
        });

        setIsVerifyingEmail(true);
        const payload = {
          file: fileData?.file,
          mapConfig: transformedMap,
          totalRecords: fileData?.importedContactsCount,
        };
        // upload api call
        uploadedFileCsvRequests(payload);
      },
      [fileData, importConfigParam],
    );

    // api call to get contact field and available credit
    useEffect(() => {
      getContactFieldsRequests();
      getEmailVerificationCreditsRequests();
    }, []);

    useEffect(() => {
      if (
        sendPurchaseEmailVerificationCreditsRequestStatus ===
          RequestStatus.Succeeded &&
        sendPurchaseEmailVerificationCreditsRequestMessage
      ) {
        if (getPurchaseEmailVerificationCreditsResponse.requires_action) {
          handle3dSecureCard(getPurchaseEmailVerificationCreditsResponse);
        } else {
          toaster.success(sendPurchaseEmailVerificationCreditsRequestMessage);
          getEmailVerificationCreditsRequests();
          hidePurchaseCreditsModal();
          resetPurchaseEmailVerificationCreditsResponses();
        }
      }
    }, [sendPurchaseEmailVerificationCreditsRequestStatus]);

    useEffect(() => {
      executeOnRequestStatus({
        status: uploadedFileCsvRequestStatus,
        onSuccess: () => {
          setIsVerifyingEmail(false);
          setFileData(null);
          setShowModal(false);
          enableBulkActionRequestPollings();
          getEmailVerificationCreditsRequests();
          resetUploadedFileCsvRequests();
          setSearchPagination({
            page: 1,
            limit: 25,
            search: '',
          });
        },
        onFailed: () => {
          setIsVerifyingEmail(false);
          setFileData(null);
          setShowModal(false);
          resetUploadedFileCsvRequests();
        },
      });
    }, [uploadedFileCsvRequestStatus]);

    return (
      <>
        <Modal
          show={showModal}
          title={
            <>
              <span>Import Email to Verify</span>
              <span
                className="email-credits"
                {...accessibleOnClick(() => setShowPurchaseCreditModel(true))}
              >
                <Diamond className="email-diamond-icon" />
                {`${emailVerifyCredits} Credits`}
              </span>
            </>
          }
          onHide={() => {
            setShowModal(false);
            setIsVerifyingEmail(false);
            setFileData(null);
          }}
          className="file-upload-module-container"
          hideFooter
          showCloseButton
        >
          <ImportStageTabs
            stage={stage}
            onStageChange={onStageChange}
            showUpdatedStageOptions
          />

          {stage === Stage?.MapFields && (
            <MapFields
              headers={fileData?.headers}
              firstRow={fileData?.firstRow?.map(
                (el): RowDataWithTypeVerification => ({
                  value: el,
                  isTypeValid: null,
                }),
              )}
              fields={emailFilteredFields}
              submitBtnDisable={isVerifyingEmail || emailVerifyCredits === 0}
              isRequestPending={false}
              steps={[]}
              onClose={() => setShowPurchaseCreditModel(true)}
              onFinish={mapFieldsFinishHandler}
              text={
                <div
                  className={`map-field-modal-footer ${
                    emailVerifyCredits >= fileData?.importedContactsCount ||
                    emailVerifyCredits === 0
                      ? 'map-field-info-banner'
                      : ''
                  }`}
                >
                  <ImportEmailVerification
                    availableCredits={emailVerifyCredits}
                    requiredCredits={fileData?.importedContactsCount}
                  />
                  {emailVerifyCredits < fileData?.importedContactsCount &&
                    emailVerifyCredits !== 0 && (
                      <div className="map-field-info">
                        <AlertCircle className="alert-circle-icon" />
                        <span>
                          Only {emailVerifyCredits} record
                          {emailVerifyCredits > 1 && 's'} will be verify. To
                          verify all buy more credits{' '}
                        </span>
                      </div>
                    )}
                </div>
              }
              cancelText="Buy Credits"
              nextBtnText={
                isVerifyingEmail ? (
                  <>
                    <Spinner
                      animation="border"
                      className="map-field-verify-spinner"
                    />
                    Verifying
                  </>
                ) : (
                  'Verify'
                )
              }
            />
          )}
        </Modal>

        {showPurchaseCreditModel && (
          <PurchaseCreditsModal
            type="email-verification"
            show={showPurchaseCreditModel}
            onClose={hidePurchaseCreditsModal}
            onBuyCredits={(stripeCardToken, planId) =>
              purchaseEmailVerificationCreditsRequests({
                stripeCardToken,
                planId,
              })
            }
            isPurchaseEmailVerificationCreditsLoading={
              sendPurchaseEmailVerificationCreditsRequestStatus ===
                RequestStatus.Pending || is3dSecureResponseStatePending
            }
            currentPlan={currentPlanName}
            evCreditPlans={getEmailVerificationPlanResponse}
            onCheckoutComplete={handleCheckoutComplete}
            onPaymentFailed={handleTransactionFailed}
          />
        )}
      </>
    );
  },
);

export default FileUpload;
